import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { UploadedFile } from './uploaderTypes';

interface EvaluatedFileItemProps {
  file: UploadedFile;
  onDelete: (id: string) => void;
}

/**
 * Renders a single uploaded file row in its post-evaluation state
 * (valid ✓ or invalid ✗ with a short AI-generated summary).
 */
const EvaluatedFileItem: React.FC<EvaluatedFileItemProps> = ({ file, onDelete }) => {
  const isValid = file.status === 'Success';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        py: 1.5,
        backgroundColor: '#FBFBFB',
        borderRadius: '8px',
      }}
    >
      {/* Top row: filename + status icon + delete */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          title={file.name}
          sx={{
            fontSize: 15,
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 500,
            color: '#131426',
            flex: 1,
            mr: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {file.name}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isValid ? (
            <img src="/images/SuccessHover.svg" alt="correct file" width={14} height={14} />
          ) : (
            <img src="/images/CrossHover.svg" alt="incorrect file" width={14} height={14} />
          )}

          <IconButton
            size="small"
            onClick={() => onDelete(file.id)}
            sx={{
              cursor: 'pointer',
            }}
          >
            <img src="/images/trash-icon-blue.svg" alt="delete-file" width={14} height={14} />
          </IconButton>
        </Box>
      </Box>

      {/* Summary / reason row */}
      {(file.summary || file.invalidReason) && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, pl: 0.25 }}>
          {/* Spark icon */}
          <Box
            component="span"
            sx={{
              fontSize: 11,
              color: '#1A55F9',
              lineHeight: '20px',
              flexShrink: 0,
              mr: 1.5,
            }}
          >
            <img src="/images/stars-new.svg" alt="stars-icon" width={14} height={14} />
          </Box>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 400,
              color: '#333333',
              fontFamily: 'Poppins, sans-serif',
              lineHeight: 1.5,
            }}
          >
            {isValid ? file.summary : file.invalidReason}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EvaluatedFileItem;
